import { getCachedGymId } from './gymContext';

function storageKey(gymId) {
  return `gym:${gymId || 'default'}:receipt-print-serial`;
}

function assignedKey(gymId) {
  return `gym:${gymId || 'default'}:receipt-print-serial-map`;
}

function readCounter(gymId) {
  if (typeof window === 'undefined') return 0;
  try {
    const n = Number(localStorage.getItem(storageKey(gymId)));
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  } catch {
    return 0;
  }
}

function writeCounter(gymId, value) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(storageKey(gymId), String(Math.max(0, Number(value) || 0)));
}

function readAssignedMap(gymId) {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(assignedKey(gymId));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeAssignedMap(gymId, map) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(assignedKey(gymId), JSON.stringify(map || {}));
}

function receiptIdentity(receipt = {}) {
  return String(
    receipt.invoiceId
    || receipt.paymentId
    || receipt.id
    || receipt.rawId
    || '',
  ).trim();
}

/** Format as REC-0001, REC-0002, … */
export function formatReceiptSerial(n) {
  const num = Math.max(1, Math.floor(Number(n) || 1));
  return `REC-${String(num).padStart(4, '0')}`;
}

/**
 * Next print serial for this gym (increments permanently).
 * Same invoice/payment keeps the same serial on reprint.
 */
export function allocateReceiptPrintSerial(receipt = {}, explicitGymId = '') {
  const gymId = explicitGymId || getCachedGymId() || 'default';
  const identity = receiptIdentity(receipt);

  if (receipt.receiptSerial || receipt.printSerial) {
    return String(receipt.receiptSerial || receipt.printSerial);
  }

  if (identity) {
    const map = readAssignedMap(gymId);
    if (map[identity]) return String(map[identity]);
  }

  const next = readCounter(gymId) + 1;
  writeCounter(gymId, next);
  const serial = formatReceiptSerial(next);

  if (identity) {
    const map = readAssignedMap(gymId);
    map[identity] = serial;
    writeAssignedMap(gymId, map);
  }

  return serial;
}

/** Attach a print serial to receipt data (mutates a shallow copy). */
export function withReceiptPrintSerial(receipt = {}, explicitGymId = '') {
  const serial = allocateReceiptPrintSerial(receipt, explicitGymId);
  return {
    ...receipt,
    receiptSerial: serial,
    printSerial: serial,
  };
}

/**
 * Look up an already-allotted print serial without creating a new one.
 * Used by list/PDF exports so bulk export does not burn REC numbers.
 */
export function peekReceiptPrintSerial(receipt = {}, explicitGymId = '') {
  if (receipt.receiptSerial || receipt.printSerial) {
    return String(receipt.receiptSerial || receipt.printSerial);
  }
  const gymId = explicitGymId || getCachedGymId() || 'default';
  const identity = receiptIdentity(receipt);
  if (!identity) return '';
  const map = readAssignedMap(gymId);
  return map[identity] ? String(map[identity]) : '';
}
